// Libraries
import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
// Components
import DeviceAccordionStyles from 'components/admin/businesses/devices/DeviceAccordion.module.scss';
import { EmptyList } from 'components/admin/businesses/devices/EmptyList';
import Actions from 'components/admin/deviceDashboard/deviceTable/components/Actions';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
// API
import { fetchDevices } from 'api/admin/businesses/businesses';
import { ReactComponent as ExpandMore } from 'assets/images/icon_expand_more.svg';
import { ReactComponent as FolderIcon } from 'assets/images/icon_folder.svg';

const DEVICES_COLUMNS = [
  {
    headerName: 'Device ID',
    field: 'name',
    flex: 1,
    renderCell: ({ row: { id, name } }) => <Link to={`/devices/${id}`}>{name}</Link>,
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Assigned On',
    type: 'date',
    valueFormatter: ({ value }) => moment(value).format('D-MMM-YYYY'),
  },
  { headerName: 'Configuration Status', field: 'configStatus', flex: 1 },
  { headerName: 'Active', field: 'isActive', type: 'boolean' },
  { headerName: 'Paired', field: 'isPaired', type: 'boolean' },
  { headerName: 'Status', field: 'status', flex: 1 },
  {
    headerName: 'Action',
    field: 'action',
    flex: 1,
    sortable: false,
    renderCell: ({ row: { name } }) => <Actions mac={name} />,
  },
];

export function DeviceAccordion({ batch }) {
  const { id: businessOwnerId } = useParams();
  const [devicePage, setDevicePage] = useState(0);
  const page = devicePage + 1;
  const [batchIdToFetch, setBatchIdToFetch] = useState(null);

  const {
    data: devices,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['devices', businessOwnerId, batch.id, page],
    queryFn: () => fetchDevices(businessOwnerId, { id: batch.id, page }),
    enabled: batch.id === batchIdToFetch,
  });

  const devicesError = error?.response?.data?.error ?? error?.message ?? '';

  const deviceList = devices?.data?.devices ?? [];
  const totalPageCount = devices?.data?.totalPage ?? 0;
  const batchIdText = `Batch Id: ${batch.id}`;
  const deviceCountText = `Devices: ${batch.deviceCount}`;
  const assignedOnText = `Assigned On: ${moment(batch.createdAt).format('D-MMM-YYYY')}`;

  const handleAccordionChange = (event, isExpanded) => {
    if (isExpanded) {
      setBatchIdToFetch(batch.id);
    } else {
      setBatchIdToFetch(null);
    }
  };

  return (
    <Accordion onChange={handleAccordionChange} classes={{ root: DeviceAccordionStyles.deviceAccordion }}>
      <AccordionSummary expandIcon={<ExpandMore className={DeviceAccordionStyles.icon} />}>
        <div className={DeviceAccordionStyles.customerDetails}>
          <div className={DeviceAccordionStyles.batchId}>
            <FolderIcon />
            <span>{batchIdText}</span>
          </div>
          <div className={DeviceAccordionStyles.leftLine}>{deviceCountText}</div>
          <div className={DeviceAccordionStyles.leftLine}>{assignedOnText}</div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={DeviceAccordionStyles.accordionDetails}>
        <CentsDataGridServer
          components={{
            NoRowsOverlay: EmptyList,
            NoResultsOverlay: EmptyList,
          }}
          componentsProps={{
            noRowsOverlay: { error: devicesError },
            noResultsOverlay: { error: devicesError },
          }}
          columns={DEVICES_COLUMNS}
          data={deviceList ?? []}
          isLoading={isLoading}
          onPageChange={({ page }) => setDevicePage(page)}
          page={devicePage}
          pageSize={10}
          rowCount={totalPageCount * 10}
        />
      </AccordionDetails>
    </Accordion>
  );
}

DeviceAccordion.propTypes = {
  batch: PropTypes.shape({
    createdAt: PropTypes.string.isRequired,
    deviceCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    isAssigned: PropTypes.bool.isRequired,
  }),
};
